import { format, formatISO, parseISO, type Locale } from "date-fns";
import { ru } from "date-fns/locale";

// Numbers and money are formatted only by `lib/format` — the app's one
// formatter. The date helpers below stay here: they lean on date-fns patterns.

export function formatDate(
  input: Date | string | number,
  pattern = "d MMM yyyy",
  locale: Locale = ru,
): string {
  const d =
    typeof input === "string"
      ? /^\d{4}-\d{2}-\d{2}$/.test(input)
        ? parseISO(input)
        : new Date(input)
      : input instanceof Date
        ? input
        : new Date(input);
  if (Number.isNaN(d.getTime())) return "—";
  return format(d, pattern, { locale });
}

/**
 * A moment in a log, as a person reads it: `21.09 16:53:05`.
 *
 * The click and conversion journals printed the wire value —
 * `2026-09-21T13:53:05.803Z` — in a narrow column, so the operator had to
 * parse an ISO string and shift the zone in his head to answer "was this
 * before or after I raised the bid". `timeZone` is the report's zone, the
 * same one the numbers beside it are bucketed by; without that the row would
 * say one hour and the chart another.
 *
 * The year is left out on purpose: a log window is days wide, and the column
 * it lives in is not. An unparseable value renders as an em dash rather than
 * as `Invalid Date`.
 */
export function formatLogTime(input: string | undefined, timeZone?: string): string {
  if (!input) return "—";
  const d = new Date(input);
  if (Number.isNaN(d.getTime())) return "—";
  try {
    return new Intl.DateTimeFormat("ru-RU", {
      day: "2-digit",
      month: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      timeZone,
      hour12: false,
    }).format(d);
  } catch {
    // An unknown IANA zone throws. The reader is better served by the same
    // moment in his browser's zone than by a crash inside a table cell.
    return new Intl.DateTimeFormat("ru-RU", {
      day: "2-digit",
      month: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
    }).format(d);
  }
}

export function formatDateRange(from: string, to: string, pattern = "d MMM yyyy", locale: Locale = ru): string {
  return `${formatDate(from, pattern, locale)} – ${formatDate(to, pattern, locale)}`;
}

export function toISODateString(d: Date): string {
  return formatISO(d, { representation: "date" });
}

/** Parse decimal string to number; NaN becomes 0. */
export function parseDecimalString(value: string | undefined): number {
  if (value === undefined || value === "") return 0;
  const n = Number.parseFloat(value);
  return Number.isFinite(n) ? n : 0;
}

/**
 * «14.10 13:58» (or «13:58:12» with `clock`) — a journal row's moment in the
 * workspace's zone, the way доски Conversions and Postbacks print it. A row
 * from another day keeps its date in clock mode, so a list that crosses
 * midnight never shows two «13:58» that are a day apart. Pure.
 */
export function formatStamp(input: string | undefined, timeZone: string, opts: { clock?: boolean; now?: Date } = {}): string {
  if (!input) return "—";
  const d = new Date(input);
  if (Number.isNaN(d.getTime())) return "—";
  const parts = (x: Date) => {
    try {
      const f = new Intl.DateTimeFormat("ru-RU", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false, timeZone });
      return Object.fromEntries(f.formatToParts(x).map((p) => [p.type, p.value]));
    } catch {
      const f = new Intl.DateTimeFormat("ru-RU", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false });
      return Object.fromEntries(f.formatToParts(x).map((p) => [p.type, p.value]));
    }
  };
  const p = parts(d);
  const hm = `${p.hour}:${p.minute}`;
  if (!opts.clock) return `${p.day}.${p.month} ${hm}`;
  const today = parts(opts.now ?? new Date());
  const time = `${hm}:${p.second}`;
  return p.day === today.day && p.month === today.month ? time : `${p.day}.${p.month} ${time}`;
}

/**
 * A partner's postback address as the partner pastes it: the network's path
 * (`/pb/{network}/{token}`, served by the edge on every tracking domain) on the
 * campaign's own domain. Without a domain the bare path is all there is. Pure.
 */
export function postbackUrlOn(fqdn: string | undefined, path: string): string {
  if (!path) return "";
  if (/^https?:\/\//.test(path) || !fqdn) return path;
  return `https://${fqdn}${path.startsWith("/") ? "" : "/"}${path}`;
}
